import { openDB } from 'idb';

const DB_NAME = 'ceres-echo-cache';
const DB_VERSION = 6;
const STORE_EFFLUENT = 'effluent-chart';
const STORE_SOURCE_ID = 'source-id-map';
const STORE_OUTFALL_LOCATIONS = 'outfall-locations';
const TTL_MS = 24 * 60 * 60 * 1000;

interface EffluentEntry {
  sourceId: string;
  data: any;
  cachedAt: number;
}

export interface FacilityRecord {
  sourceId: string;
  receivingWater: string;
  huc12: string;
  facilityName: string;
  lat: number | null;
  lon: number | null;
}

interface SourceIdEntry {
  frsId: string;
  facilities: FacilityRecord[];
  cachedAt: number;
}

export type OutfallLocationMap = Record<string, { lat: number; lon: number }>;

interface OutfallLocationEntry {
  sourceId: string;
  locations: OutfallLocationMap;
  cachedAt: number;
}

function openCacheDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      if (oldVersion < 3) {
        // Drop prior stores to invalidate entries lacking FacLat/FacLong
        if (db.objectStoreNames.contains(STORE_SOURCE_ID)) db.deleteObjectStore(STORE_SOURCE_ID);
        if (db.objectStoreNames.contains(STORE_EFFLUENT)) db.deleteObjectStore(STORE_EFFLUENT);
      }
      if (oldVersion < 5 && db.objectStoreNames.contains(STORE_OUTFALL_LOCATIONS)) {
        // Drop OECA-only entries from v4 — v5 merges TCEQ for TX permits
        db.deleteObjectStore(STORE_OUTFALL_LOCATIONS);
      }
      if (oldVersion < 6 && db.objectStoreNames.contains(STORE_SOURCE_ID)) {
        // Drop entries cached with placeholder HUC-12 / receiving water from the
        // wrong ECHO qcolumns (165/164 instead of 166 FacDerivedWBD / 167 FacDerivedWBDName)
        db.deleteObjectStore(STORE_SOURCE_ID);
      }
      if (!db.objectStoreNames.contains(STORE_EFFLUENT)) {
        db.createObjectStore(STORE_EFFLUENT, { keyPath: 'sourceId' });
      }
      if (!db.objectStoreNames.contains(STORE_SOURCE_ID)) {
        db.createObjectStore(STORE_SOURCE_ID, { keyPath: 'frsId' });
      }
      if (!db.objectStoreNames.contains(STORE_OUTFALL_LOCATIONS)) {
        db.createObjectStore(STORE_OUTFALL_LOCATIONS, { keyPath: 'sourceId' });
      }
    },
  });
}

function isExpired(cachedAt: number): boolean {
  return Date.now() - cachedAt > TTL_MS;
}

export async function getCachedSourceId(frsId: string): Promise<FacilityRecord[] | null> {
  try {
    const db = await openCacheDB();
    const entry: SourceIdEntry | undefined = await db.get(STORE_SOURCE_ID, frsId);
    if (!entry || isExpired(entry.cachedAt)) return null;
    return entry.facilities;
  } catch {
    return null;
  }
}

export async function setCachedSourceId(
  frsId: string,
  facilities: FacilityRecord[]
): Promise<void> {
  try {
    const db = await openCacheDB();
    const entry: SourceIdEntry = { frsId, facilities, cachedAt: Date.now() };
    await db.put(STORE_SOURCE_ID, entry);
  } catch {
    // cache write failure is non-fatal
  }
}

export async function getCachedEffluent(sourceId: string): Promise<any | null> {
  try {
    const db = await openCacheDB();
    const entry: EffluentEntry | undefined = await db.get(STORE_EFFLUENT, sourceId);
    if (!entry || isExpired(entry.cachedAt)) return null;
    return entry.data;
  } catch {
    return null;
  }
}

export async function setCachedEffluent(sourceId: string, data: any): Promise<void> {
  try {
    const db = await openCacheDB();
    const entry: EffluentEntry = { sourceId, data, cachedAt: Date.now() };
    await db.put(STORE_EFFLUENT, entry);
  } catch {
    // cache write failure is non-fatal
  }
}

export async function getCachedOutfallLocations(
  sourceId: string
): Promise<OutfallLocationMap | null> {
  try {
    const db = await openCacheDB();
    const entry: OutfallLocationEntry | undefined = await db.get(STORE_OUTFALL_LOCATIONS, sourceId);
    if (!entry || isExpired(entry.cachedAt)) return null;
    return entry.locations;
  } catch {
    return null;
  }
}

export async function setCachedOutfallLocations(
  sourceId: string,
  locations: OutfallLocationMap
): Promise<void> {
  try {
    const db = await openCacheDB();
    const entry: OutfallLocationEntry = { sourceId, locations, cachedAt: Date.now() };
    await db.put(STORE_OUTFALL_LOCATIONS, entry);
  } catch {
    // cache write failure is non-fatal
  }
}
